<script lang="ts">
	import { onMount, onDestroy } from 'svelte';
	import 'chartjs-adapter-moment';

	import {
		Chart,
		LineController,
		LineElement,
		PointElement,
		LinearScale,
		Title,
		CategoryScale,
		Filler,
		Tooltip,
		TimeScale
	} from 'chart.js';
	import { portfolioValue, userPortfolioPoints } from './store';
	import { getNumBackInUnits } from '$lib/util/util';
	import BigNumber from 'bignumber.js';

	Chart.register(
		LineController,
		LineElement,
		PointElement,
		LinearScale,
		Title,
		CategoryScale,
		Filler,
		Tooltip,
		TimeScale
	);

	export let gradientLineLength = 400;
	Chart.defaults.scale.grid.display = false;
	let isChartCreated = false;
	let currentPortfolioValue: string = '0';

	const unsubscribe = portfolioValue.subscribe((state) => {
		if (state) {
			currentPortfolioValue = state;
		}
	});

	unsubscribe();

	const getOrCreateTooltip = (chart) => {
		let tooltipEl = chart.canvas.parentNode.querySelector('div');

		if (!tooltipEl) {
			tooltipEl = document.createElement('div');
			tooltipEl.style.opacity = 1;
			tooltipEl.style.pointerEvents = 'none';
			tooltipEl.style.position = 'absolute';
			tooltipEl.style.transform = 'translate(-50%, 0)';
			tooltipEl.style.transition = 'all .1s ease';

			const innerTooltip = document.createElement('div');

			tooltipEl.appendChild(innerTooltip);
			chart.canvas.parentNode.appendChild(tooltipEl);
		}

		return tooltipEl;
	};

	const externalTooltipHandler = (context) => {
		// Tooltip Element
		const { chart, tooltip } = context;
		const tooltipEl = getOrCreateTooltip(chart);

		// Hide if no tooltip
		if (tooltip.opacity === 0) {
			tooltipEl.style.opacity = 0;
			return;
		}

		if (tooltip.body) {
			const tooltipRoot = tooltipEl.querySelector('div');

			// Remove old children
			while (tooltipRoot.firstChild) {
				tooltipRoot.firstChild.remove();
			}

			// Add new children
			tooltipRoot.innerHTML =
				`
              <div class="p-px bg-fx-gradient-6 rounded-xl squircle-xl">
                  <div class="bg-fx-black backdrop-filter backdrop-blur-sm opacity-70 p-5 rounded-xl squircle-xl">
                      <div class="border-b-2 border-fx-grey-800 flex justify-between">
                          <div class="pb-3 mr-12 text-fx-gradient-4 tex-sm font-semibold">Asset</div>
                          
                          <div class="p-px bg-fx-gradient-4 rounded-full h-6 w-6">
                              <div class="flex justify-center items-center bg-fx-gradient-7 w-full h-full rounded-xl squircle-xl">
                                  <img class="h-5" src='/eth-small.svg'>
                              </div>
                          </div>
                      </div>
                      <div class="flex justify-between items-center">
                          <div class="pt-3 text-fx-grey-400 text-xs">Time:</div>
                          <div class="pt-3 text-fx-grey-200 text-sm">` +
				`</div>
                      </div>
                      <div class="flex justify-between items-center">
                          <div class="text-fx-grey-400 text-xs">Price:</div>
                          <div class="text-fx-yellow text-sm">$` +
				`</div>
                      </div>
                  </div>
              </div>
              `;
		}

		const { offsetLeft: positionX, offsetTop: positionY } = chart.canvas;

		// Display, position, and set styles for font
		tooltipEl.style.opacity = 1;
		tooltipEl.style.left = positionX + tooltip.caretX + 'px';
		tooltipEl.style.top = positionY + tooltip.caretY + 'px';
		tooltipEl.style.font = tooltip.options.bodyFont.string;
	};

	async function createChart() {
		let yieldData: any = [];
		let suggestedMin: number = 1000000000000000000000000;
		let suggestedMax: number = 0;
		const currentPortfolioPoint = {
			timestamp: Date.now(),
			portfolioValue: currentPortfolioValue,
			pnl: $userPortfolioPoints[0].pnl
		};
		const _userPortfolioPoints = [currentPortfolioPoint, ...$userPortfolioPoints];
		_userPortfolioPoints.forEach(function (c) {
			var obj = {
				x: c.timestamp,
				y: getNumBackInUnits(c.portfolioValue, 18, 2)
			};
			yieldData.push(obj);
			if (parseFloat(obj.y) > suggestedMax) {
				suggestedMax = parseFloat(obj.y);
			}
			if (parseFloat(obj.y) < suggestedMin) {
				suggestedMin = parseFloat(obj.y);
			}
		});
		suggestedMin = suggestedMax * 0.95;
		suggestedMax = suggestedMax * 1.02;
		yieldData.reverse();
		let perChart = document.getElementById('userPortfolioChartElement');
		if (!perChart) return;
		var ctx = perChart.getContext('2d');

		var gradient = ctx.createLinearGradient(0, 0, 0, gradientLineLength);
		gradient.addColorStop(1, 'rgba(246, 248, 255, 0)');
		gradient.addColorStop(0, 'rgba(149, 161, 205, 0.15)');

		var chart = new Chart(ctx, {
			type: 'line',
			data: {
				datasets: [
					{
						fill: 'start',
						pointRadius: 0,
						backgroundColor: gradient,
						borderColor: '#F6F8FF',
						borderWidth: 1,
						data: yieldData,
						pointHoverRadius: 5,
						pointHoverBackgroundColor: '#FFD99F'
					}
				]
			},
			options: {
				animation: {
					duration: 0
				},
				maintainAspectRatio: false,
				responsive: true,
				interaction: {
					intersect: false
				},
				scales: {
					x: {
						display: true,
						type: 'time',
						time: {
							tooltipFormat: 'DD MMM YY',
							minUnit: 'day'
						}
					},

					y: {
						display: true,
						suggestedMax: suggestedMax,
						suggestedMin: suggestedMin
					}
				},
				plugins: {
					tooltip: {
						displayColors: false,
						callbacks: {
							label: function (context) {
								let label = context.dataset.label || '';

								if (label) {
									label += ': ';
								}
								if (context.parsed.y !== null) {
									label += new Intl.NumberFormat('en-US', {
										style: 'currency',
										currency: 'USD'
									}).format(context.parsed.y);
								}
								return label;
							}
						}
					}
				}
			}
		});
		isChartCreated = true;
		return chart;
	}

	async function updateChartData() {
		let yieldData: any = [];
		const currentPortfolioPoint = {
			timestamp: Date.now(),
			portfolioValue: currentPortfolioValue,
			pnl: $userPortfolioPoints[0].pnl
		};
		const _userPortfolioPoints = [currentPortfolioPoint, ...$userPortfolioPoints];
		_userPortfolioPoints.forEach(function (c) {
			var obj = {
				x: c.timestamp,
				y: getNumBackInUnits(c.portfolioValue, 18, 2)
			};
			yieldData.push(obj);
		});
		yieldData.reverse();
		let perChart = document.getElementById('userPortfolioChartElement');
		if (!perChart) return;
		var ctx = perChart.getContext('2d');

		var gradient = ctx.createLinearGradient(0, 0, 0, gradientLineLength);
		gradient.addColorStop(1, 'rgba(246, 248, 255, 0)');
		gradient.addColorStop(0, 'rgba(149, 161, 205, 0.15)');
		userPortfolioChart.data = {
			datasets: [
				{
					fill: 'start',
					pointRadius: 0,
					backgroundColor: gradient,
					borderColor: '#F6F8FF',
					borderWidth: 1,
					data: yieldData,
					pointHoverRadius: 5,
					pointHoverBackgroundColor: '#FFD99F'
				}
			]
		};
		userPortfolioChart.update();
	}

	//holds reference to chart so that it can be destroyed when range is changed to add a new chart
	let userPortfolioChart: any;

	let userPortfolioPointsListener: any;
	let portfolioValueListener: any;

	function checkIfPortfolioValueChanged(newVal: string) {
		if (new BigNumber(currentPortfolioValue).minus(newVal).abs().gt(1e18)) {
			currentPortfolioValue = newVal;
			updateChartData();
		}
	}

	onMount(async () => {
		userPortfolioPointsListener = userPortfolioPoints.subscribe(async (state) => {
			if (state != undefined) {
				if (userPortfolioChart == undefined) {
					userPortfolioChart = await createChart();
				} else {
					updateChartData();
				}
			}
		});

		portfolioValueListener = portfolioValue.subscribe((state) => {
			if (state && userPortfolioChart) {
				checkIfPortfolioValueChanged(state);
			}
		});
	});

	onDestroy(() => {
		userPortfolioPointsListener();
		portfolioValueListener();
	});
</script>

<div class="h-96 sm:h-96">
	<canvas class="w-full {!isChartCreated ? 'hidden' : 'block'}" id="userPortfolioChartElement" />
</div>
